import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.css';
import './HandsontableDark.css';
import { TextField, Paper, Box, Alert, Snackbar, FormControlLabel, Switch, Typography, Button, Tooltip } from '@mui/material';
import { Fullscreen as FullscreenIcon } from '@mui/icons-material';
import Handsontable from 'handsontable';  // ✅ Import entire Handsontable core
import GuidedEntry from './GuidedEntry';
import { apiFetch } from '../api/apiFetch';
import { apiErrorMessage } from '../api/apiError';


// ✅ Register the correct numeric editor
const { NumericEditor } = Handsontable.editors;

// FIX-9: `dataset.usl`/`dataset.lsl` arrive here as one of: a genuine JS
// number, a pg NUMERIC string (history reload), or null/undefined/'' (no
// limit entered on that side). A bare `dataset.usl || ''` treats the
// *number* 0 (and, in principle, the string '0') as falsy and silently
// drops it — but 0 is a legitimate spec limit (e.g. a flatness callout with
// LSL=0). Only a genuinely-absent value should normalize to '' (the "no
// limit entered" sentinel the USL/LSL TextFields expect); everything else —
// including 0 / '0' — passes through unchanged.
const normalizeSpecLimit = (v) => (v === null || v === undefined || v === '' ? '' : v);

// UX-9: cap how much of a submit error gets lifted to the parent/ChartSection.
// The raw message can be a server-joined `data.errors` array or a long
// human-readable sentence — both are too much for ChartSection's compact
// stalled-chart line, so trim to a short, still-identifiable snippet.
// 120 (not 80): every first-party message must render WHOLE — the most likely
// auto-run failure, apiErrorMessage's 5xx warm-up copy, is 85 chars and was
// getting clipped mid-sentence at 80. 120 fits all known app messages while
// still capping raw server text.
const MAX_LIFTED_ERROR_LEN = 120;
const summarizeSubmitError = (message) => {
    if (!message) return null;
    const trimmed = String(message).trim();
    return trimmed.length > MAX_LIFTED_ERROR_LEN
        ? `${trimmed.slice(0, MAX_LIFTED_ERROR_LEN - 1)}…`
        : trimmed;
};

const DataEntryTable = forwardRef(({ onNewDataset, dataset, onLoadingChange, onSubmitError }, ref) => {
    const [description, setDescription] = useState('');
    const [usl, setUsl] = useState('');
    const [lsl, setLsl] = useState('');
    const [useSubgroups, setUseSubgroups] = useState(false); // Toggle: false = individual, true = subgroups
    const [values, setValues] = useState(Array.from({ length: 30 }, () => [null, null])); // [subgroupId, value]
    const [loading, setLoading] = useState(false);

    // Report submit-in-progress up so the parent can disable/spin the Submit button.
    useEffect(() => { if (onLoadingChange) onLoadingChange(loading); }, [loading, onLoadingChange]);
    const [error, setError] = useState(null);
    // UX-9: the error Alert below lives inside the Data Entry drawer, which is
    // closed by default on mobile — a failed submit (including the first-visit
    // auto-submit) would otherwise be invisible except for ChartSection's
    // generic "stalled" copy. Lift a concise, sanitized copy of the error up so
    // CockpitPage can thread it into that stalled message. Mirrors the
    // onLoadingChange idiom exactly; fires on mount (null) and clears back to
    // null the moment any new submit starts (handleSubmit's `setError(null)`),
    // including one that goes on to succeed.
    useEffect(() => { if (onSubmitError) onSubmitError(summarizeSubmitError(error)); }, [error, onSubmitError]);
    const [warnings, setWarnings] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // Guided entry mode state (individual measurements only)
    const [guidedMode, setGuidedMode] = useState(false);
    const [guidedValues, setGuidedValues] = useState([]);

    // UX-8: when a seeded `dataset` is flagged `autoSubmit` (first-visit example),
    // this flag queues Submit to fire once the state seeded below has actually
    // committed and re-rendered — see the ready-effect after this one. This is a
    // real ready-signal (observed state) rather than a guessed wall-clock delay.
    //
    // Deliberately STATE, not a ref: sibling effects in the same commit do not see
    // each other's queued setState calls (only the *next* render does), so setting
    // this flag in the same effect/render as the seeded fields means both become
    // visible together on that next render — the ready-effect below then reads the
    // co-committed, freshly-seeded values/usl/lsl/description in the same pass. A
    // ref would flip synchronously and fire one render too early, reading stale
    // (pre-seed) state.
    const [pendingAutoSubmit, setPendingAutoSubmit] = useState(false);

    // Expose submit function and loading state to parent component
    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
        loading: loading
    }));

    useEffect(() => {
        if (dataset) {
            setDescription(dataset.description || '');
            setUsl(normalizeSpecLimit(dataset.usl));
            setLsl(normalizeSpecLimit(dataset.lsl));
            setUseSubgroups(dataset.subgroupSize && dataset.subgroupSize > 1);
            // Handle both old format [value] and new format [subgroupId, value]
            if (dataset.values && dataset.values.length > 0) {
                if (Array.isArray(dataset.values[0]) && dataset.values[0].length === 2) {
                    setValues(dataset.values); // New format
                } else {
                    // Old format: convert single values to [null, value]
                    setValues(dataset.values.map(v => [null, Array.isArray(v) ? v[0] : v]));
                }
            } else {
                setValues(Array.from({ length: 30 }, () => [null, null]));
            }
            setPendingAutoSubmit(!!dataset.autoSubmit);
            // A stale error from a previous submit must not survive a reseed —
            // it would keep the drawer Alert (and the UX-9 lifted submitError in
            // CockpitPage/ChartSection) pointing at a failure that no longer
            // describes the current dataset.
            setError(null);
            setWarnings([]);
        } else {
            setDescription('');
            setUsl('');
            setLsl('');
            setUseSubgroups(false);
            setValues(Array.from({ length: 30 }, () => [null, null]));
            setPendingAutoSubmit(false);
            setError(null);
            setWarnings([]);
        }
    }, [dataset]);

    // UX-8 ready-effect: fires the queued auto-submit only on the render where
    // `pendingAutoSubmit` and the seeded description/usl/lsl/values/useSubgroups
    // above are simultaneously fresh — not after a fixed setTimeout, which could
    // fire before seeding landed (or waste time after it already had). Runs at
    // most once per seeded dataset: the flag is cleared immediately, so later user
    // edits to these fields are no-ops.
    useEffect(() => {
        if (pendingAutoSubmit) {
            setPendingAutoSubmit(false);
            handleSubmit();
        }
    }, [pendingAutoSubmit, values, usl, lsl, description, useSubgroups]);

    const handleHotTableChange = (changes, source) => {
        if (!changes || source === 'loadData') return;
        const updatedValues = [...values];
        changes.forEach(([row, col, oldValue, newValue]) => {
            if (!updatedValues[row]) {
                updatedValues[row] = [null, null];
            }
            if (col === 0 && useSubgroups) {
                // Subgroup ID column - allow text or numbers
                updatedValues[row][0] = newValue !== '' ? newValue : null;
            } else if (col === 1 || (col === 0 && !useSubgroups)) {
                // Value column - parse as number (column 1 for subgroups, column 0 for individual but stored at index 1)
                updatedValues[row][1] = newValue !== '' ? parseFloat(newValue) : null;
            }
        });
        setValues(updatedValues);
    };

	const handleSubmit = async (event) => {
    		if (event) {
    			event.preventDefault();
    		}
    		setLoading(true);
    		setError(null);
    		setWarnings([]);

    		try {
        		// Prepare data based on subgroup toggle
        		let dataToSend;
        		if (!useSubgroups) {
            			// Individual measurements: extract just the values (second column)
            			const numericValues = values.map(row => row[1]).filter(value => !isNaN(value) && value !== null);
            			if (numericValues.length === 0) {
                			setError('Please enter valid numerical data.');
                			setLoading(false);
                			return;
            			}
            			dataToSend = {
                			description: description.trim(),
                			values: numericValues,
                			usl: usl !== '' ? Number(usl) : null,
                			lsl: lsl !== '' ? Number(lsl) : null,
                			subgroupSize: 1
            			};
        		} else {
            			// Subgroup data: group by subgroup ID
            			const subgroupData = {};
            			values.forEach(([subgroupId, value]) => {
                			if (subgroupId !== null && subgroupId !== '' && !isNaN(value) && value !== null) {
                    				if (!subgroupData[subgroupId]) {
                        					subgroupData[subgroupId] = [];
                    				}
                    				subgroupData[subgroupId].push(value);
                			}
            			});

            			const subgroups = Object.keys(subgroupData);
            			if (subgroups.length === 0) {
                			setError('Please enter valid subgroup IDs and values.');
                			setLoading(false);
                			return;
            			}

            			dataToSend = {
                			description: description.trim(),
                			subgroups: subgroupData,
                			usl: usl !== '' ? Number(usl) : null,
                			lsl: lsl !== '' ? Number(lsl) : null,
                			subgroupSize: 2  // Arbitrary value > 1 to indicate subgroup mode
            			};
        		}

        		// Validation
        		if (!description || description.trim().length === 0) {
            			setError('Please enter a dataset description.');
            			setLoading(false);
            			return;
        		}

        		const uslPresent = usl !== '' && usl !== null && usl !== undefined;
        		const lslPresent = lsl !== '' && lsl !== null && lsl !== undefined;
        		if (!uslPresent && !lslPresent) {
            			setError('Please enter at least one specification limit (USL or LSL).');
            			setLoading(false);
            			return;
        		}
        		if (uslPresent && isNaN(Number(usl))) {
            			setError('USL must be a valid number.');
            			setLoading(false);
            			return;
        		}
        		if (lslPresent && isNaN(Number(lsl))) {
            			setError('LSL must be a valid number.');
            			setLoading(false);
            			return;
        		}
        		if (uslPresent && lslPresent && Number(usl) <= Number(lsl)) {
            			setError('USL must be greater than LSL.');
            			setLoading(false);
            			return;
        		}

        		const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        		const response = await apiFetch(`${backendUrl}/api/capability`, {
            			method: 'POST',
            			headers: { 'Content-Type': 'application/json' },
            			body: JSON.stringify(dataToSend),
            			credentials: 'include'
        		});

        		const data = await response.json().catch(() => ({}));

        		if (!response.ok) {
            			// Prefer the server's own error; otherwise map 429 (throttled) /
            			// 5xx (server warming up) to a real message, not "network error".
            			setError(apiErrorMessage(response.status, data, 'Failed to submit data.'));

            			// Display warnings even if there are errors
            			if (data.warnings && Array.isArray(data.warnings)) {
                			setWarnings(data.warnings);
            			}
            			setLoading(false);
            			return;
        		}

        		// Show warnings if any (even on success)
        		if (data.warnings && Array.isArray(data.warnings)) {
            			setWarnings(data.warnings);
            			setSnackbarOpen(true);
        		}

        		// Pass the entire data object from the backend to the parent
        		onNewDataset(data);
        		setLoading(false);

        		// Show success message
        		setError(null);

    		} catch (error) {
        		console.error('Error submitting data:', error);
        		setError('Network error. Please check your connection and try again.');
        		setLoading(false);
    		}
	};

	const handleCloseSnackbar = () => {
    		setSnackbarOpen(false);
	};

    // ---------------------------------------------------------------------------
    // Guided entry handlers (individual mode only)
    // ---------------------------------------------------------------------------

    const handleEnterGuided = () => {
        // Seed guided values from current table state
        const seeded = values
            .map(row => row[1])
            .filter(v => v !== null && v !== undefined && !isNaN(v));
        setGuidedValues(seeded);
        setGuidedMode(true);
    };

    const handleGuidedSubmitValue = (val) => {
        setGuidedValues(prev => [...prev, val]);
    };

    const handleExitGuided = () => {
        // Write guided values back into the [subgroupId, value] rows structure
        const minRows = Math.max(30, guidedValues.length);
        const newRows = Array.from({ length: minRows }, (_, i) => [
            null,
            i < guidedValues.length ? guidedValues[i] : null,
        ]);
        setValues(newRows);
        setGuidedMode(false);
    };

    // Dark mode input styling for mission display aesthetic
    const inputSx = {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(20, 26, 33, 0.8)',
            '& fieldset': {
                borderColor: 'rgba(144, 202, 249, 0.2)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(144, 202, 249, 0.35)',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'rgba(144, 202, 249, 0.5)',
                borderWidth: '1px',
            },
        },
        '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.5)',
            '&.Mui-focused': {
                color: 'rgba(144, 202, 249, 0.8)',
            },
        },
        '& .MuiOutlinedInput-input': {
            color: 'rgba(255, 255, 255, 0.9)',
        },
    };

    // Render guided entry overlay (individual mode only)
    if (guidedMode && !useSubgroups) {
        return (
            <GuidedEntry
                contextLabel={description ? `Capability — ${description}` : 'Capability — Guided Entry'}
                slots={[]}
                values={guidedValues}
                onSubmitValue={handleGuidedSubmitValue}
                onClose={handleExitGuided}
                appendMode
                specLimits={{ usl, lsl }}
            />
        );
    }

    return (
        <Paper sx={{
            padding: 0,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            width: '100%',
            overflow: 'hidden',
          }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {/* Error Alert */}
                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Warnings Alert */}
                {warnings.length > 0 && (
                    <Alert severity="warning" onClose={() => setWarnings([])} sx={{ mb: 2 }}>
                        {warnings.map((warning, idx) => (
                            <div key={idx}>• {warning}</div>
                        ))}
                    </Alert>
                )}

                {/* Compact input layout */}
                <Box sx={{ flexShrink: 0, mb: 1 }}>
                    {/* Description field - full width */}
                    <TextField
                        label="Dataset Description"
                        variant="outlined"
                        size="small"
                        sx={{ ...inputSx, mb: 1 }}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                        disabled={loading}
                    />
                    {/* USL/LSL side by side - use type="text" with inputMode="decimal" for reliable numeric keypad on mobile */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                            label="LSL"
                            variant="outlined"
                            size="small"
                            sx={{ ...inputSx, flex: 1 }}
                            value={lsl}
                            onChange={(e) => setLsl(e.target.value)}
                            inputProps={{
                                inputMode: 'decimal',
                                pattern: '[0-9]*[.,]?[0-9]*',
                                autoComplete: 'off'
                            }}
                            disabled={loading}
                        />
                        <TextField
                            label="USL"
                            variant="outlined"
                            size="small"
                            sx={{ ...inputSx, flex: 1 }}
                            value={usl}
                            onChange={(e) => setUsl(e.target.value)}
                            inputProps={{
                                inputMode: 'decimal',
                                pattern: '[0-9]*[.,]?[0-9]*',
                                autoComplete: 'off'
                            }}
                            disabled={loading}
                        />
                    </Box>
                    {/* Mode toggle + guided entry button - compact */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={useSubgroups}
                                    onChange={(e) => setUseSubgroups(e.target.checked)}
                                    disabled={loading}
                                    size="small"
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: 'rgba(144, 202, 249, 0.9)' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(144, 202, 249, 0.4)' },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                    {useSubgroups ? 'Subgroups (X̄-R)' : 'Individual (I-MR)'}
                                </Typography>
                            }
                            sx={{ m: 0 }}
                        />
                        {/* Guided entry only available for individual (non-subgroup) mode */}
                        {!useSubgroups && (
                            <Tooltip title="Switch to guided fullscreen entry (keyboard wedge / mobile)">
                                <Button
                                    size="small"
                                    startIcon={<FullscreenIcon />}
                                    onClick={handleEnterGuided}
                                    disabled={loading}
                                    sx={{ fontSize: '0.7rem', color: 'rgba(144,202,249,0.7)', py: 0.25, px: 1 }}
                                >
                                    Guided Entry
                                </Button>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                {/* Handsontable container with proper scroll handling */}
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    <HotTable
                        data={values}
                        colHeaders={useSubgroups ? ['Subgroup ID', 'Value'] : ['Value']}
                        rowHeaders={true}
                        licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE || "non-commercial-and-evaluation"}
                        columns={useSubgroups ? [
                            { type: 'text', className: 'htCenter' },  // Subgroup ID column (text)
                            { type: 'numeric', editor: NumericEditor, numericFormat: { pattern: '0.00000' }, className: 'htCenter' }  // Value column (numeric)
                        ] : [
                            { data: 1, type: 'numeric', editor: NumericEditor, numericFormat: { pattern: '0.00000' }, className: 'htCenter' }  // Single value column, using index 1
                        ]}
                        afterChange={handleHotTableChange}
                        /* Mobile numeric keyboard: set inputmode on editor input when editing starts */
                        afterBeginEditing={(row, col) => {
                            // Find the active editor input and set inputmode for numeric keyboard on mobile
                            setTimeout(() => {
                                const editorInput = document.querySelector('.handsontableInput');
                                if (editorInput) {
                                    // Set inputmode for numeric columns (Value column)
                                    const isNumericCol = useSubgroups ? col === 1 : true;
                                    if (isNumericCol) {
                                        editorInput.setAttribute('inputmode', 'decimal');
                                        editorInput.setAttribute('pattern', '[0-9]*[.,]?[0-9]*');
                                    }
                                }
                            }, 0);
                        }}
                        height="100%"
                        /* Row height: single source of truth (CSS overrides for mobile via @media query) */
                        rowHeights={28}
                        minSpareRows={5}
                        manualRowResize={false}
                        contextMenu={true}
                        copyPaste={true}
                        pasteMode="overwrite"
                        allowEmpty={true}
                        allowInvalid={true}
                        autoWrapRow={true}
                        autoWrapCol={true}
                        tabMoves={{ row: 1, col: 0 }}
                        enterMoves={{ row: 1, col: 0 }}
                        autoInsertRow={true}
                        selectionMode="multiple"
                        outsideClickDeselects={false}
                        fragmentSelection={true}
                        stretchH="all"
                    />
                </Box>
            </Box>

            {/* Snackbar for success warnings */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="info" sx={{ width: '100%' }}>
                    Data submitted successfully! {warnings.length > 0 && 'Check warnings above.'}
                </Alert>
            </Snackbar>
        </Paper>
    );
});

DataEntryTable.displayName = 'DataEntryTable';

export default DataEntryTable;
